import { useCallback, useEffect, useState } from 'react';

export type TileSize = 's' | 'm' | 'l' | 'xl';
export type AspectMode = 'crop' | 'fit';
export type CaptionMode = 'always' | 'hover' | 'hidden';
export type SortMode =
  | 'newest'
  | 'oldest'
  | 'name-asc'
  | 'name-desc'
  | 'size-desc'
  | 'size-asc';

export interface GallerySettings {
  size: TileSize;
  aspect: AspectMode;
  caption: CaptionMode;
  sort: SortMode;
  imagesOnly: boolean;
}

export const DEFAULT_GALLERY_SETTINGS: GallerySettings = {
  size: 'm',
  aspect: 'crop',
  caption: 'always',
  sort: 'newest',
  imagesOnly: false,
};

// Minimum tile width in px for each size step. The grid uses
// `auto-fit, minmax(<min>, 1fr)`, so the count is browser-decided.
export const TILE_MIN_WIDTH: Record<TileSize, number> = {
  s: 120,
  m: 180,
  l: 260,
  xl: 360,
};

export const TILE_GAP: Record<TileSize, number> = {
  s: 8,
  m: 12,
  l: 16,
  xl: 20,
};

const STORAGE_KEY = 'fox-image:gallery-settings:v1';

const TILE_SIZES: TileSize[] = ['s', 'm', 'l', 'xl'];
const ASPECT_MODES: AspectMode[] = ['crop', 'fit'];
const CAPTION_MODES: CaptionMode[] = ['always', 'hover', 'hidden'];
const SORT_MODES: SortMode[] = [
  'newest',
  'oldest',
  'name-asc',
  'name-desc',
  'size-desc',
  'size-asc',
];

function sanitize(raw: unknown): GallerySettings {
  if (!raw || typeof raw !== 'object') return DEFAULT_GALLERY_SETTINGS;
  const obj = raw as Record<string, unknown>;
  const pick = <T extends string>(value: unknown, allowed: readonly T[], fallback: T): T =>
    typeof value === 'string' && (allowed as readonly string[]).includes(value)
      ? (value as T)
      : fallback;
  return {
    size: pick(obj.size, TILE_SIZES, DEFAULT_GALLERY_SETTINGS.size),
    aspect: pick(obj.aspect, ASPECT_MODES, DEFAULT_GALLERY_SETTINGS.aspect),
    caption: pick(obj.caption, CAPTION_MODES, DEFAULT_GALLERY_SETTINGS.caption),
    sort: pick(obj.sort, SORT_MODES, DEFAULT_GALLERY_SETTINGS.sort),
    imagesOnly:
      typeof obj.imagesOnly === 'boolean' ? obj.imagesOnly : DEFAULT_GALLERY_SETTINGS.imagesOnly,
  };
}

function loadFromStorage(): GallerySettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_GALLERY_SETTINGS;
    return sanitize(JSON.parse(raw));
  } catch {
    return DEFAULT_GALLERY_SETTINGS;
  }
}

function saveToStorage(settings: GallerySettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // ignore quota / disabled
  }
}

export function useGallerySettings(): {
  settings: GallerySettings;
  update: (patch: Partial<GallerySettings>) => void;
  reset: () => void;
} {
  const [settings, setSettings] = useState<GallerySettings>(DEFAULT_GALLERY_SETTINGS);

  useEffect(() => {
    setSettings(loadFromStorage());
  }, []);

  const update = useCallback((patch: Partial<GallerySettings>) => {
    setSettings((prev) => {
      const next = { ...prev, ...patch };
      saveToStorage(next);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    saveToStorage(DEFAULT_GALLERY_SETTINGS);
    setSettings(DEFAULT_GALLERY_SETTINGS);
  }, []);

  return { settings, update, reset };
}

export function compareItems<T extends { filename: string; uploaded: string; size: number }>(
  a: T,
  b: T,
  sort: SortMode,
): number {
  switch (sort) {
    case 'newest':
      return Date.parse(b.uploaded) - Date.parse(a.uploaded);
    case 'oldest':
      return Date.parse(a.uploaded) - Date.parse(b.uploaded);
    case 'name-asc':
      return a.filename.localeCompare(b.filename, undefined, { sensitivity: 'base', numeric: true });
    case 'name-desc':
      return b.filename.localeCompare(a.filename, undefined, { sensitivity: 'base', numeric: true });
    case 'size-desc':
      return b.size - a.size;
    case 'size-asc':
      return a.size - b.size;
  }
}

export const SORT_LABELS: Record<SortMode, string> = {
  newest: 'Newest first',
  oldest: 'Oldest first',
  'name-asc': 'Name A → Z',
  'name-desc': 'Name Z → A',
  'size-desc': 'Largest first',
  'size-asc': 'Smallest first',
};

export const TILE_SIZE_LABELS: Record<TileSize, string> = {
  s: 'S',
  m: 'M',
  l: 'L',
  xl: 'XL',
};

export const ASPECT_LABELS: Record<AspectMode, string> = {
  crop: 'Crop',
  fit: 'Fit',
};

export const CAPTION_LABELS: Record<CaptionMode, string> = {
  always: 'Caption on',
  hover: 'On hover',
  hidden: 'No caption',
};
